/**
 * QRShareDialog - QR Code sharing for ZiaWellNav referrals
 * 
 * Generates a QR code that links to the Telegram bot with a referral code.
 * Users who share get credits toward their renewal.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { useTranslation } from '../i18n/translations';

interface QRShareDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ReferralData {
  code: string;
  shareLink: string;
  shareText: string;
  stats: {
    totalReferrals: number;
    successfulReferrals: number;
    rewardsEarned: number;
  };
}

const QRShareDialog: React.FC<QRShareDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(false);
  
  const userId = telegram.getUserId() || 'guest';
  
  // Fetch referral code from API when dialog opens
  useEffect(() => {
    if (!open || !userId || userId === 'guest') return;
    
    const fetchReferral = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/referrals?userId=${userId}`);
        const data = await response.json();
        if (data.success && data.referral) {
          setReferralData(data.referral);
        }
      } catch (error) {
        console.error('Failed to fetch referral:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferral();
  }, [open, userId]);

  // Fallback URL if API hasn't loaded yet
  const botUrl = referralData?.shareLink || `https://t.me/ziawellnavbot?start=ref_${userId}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(botUrl);
      setCopied(true);
      feedbackService.playSound('success');
      telegram.hapticFeedback('success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareViaTelegram = () => {
    const shareText = referralData?.shareText || t('qrShareText');
    
    const text = encodeURIComponent(shareText);
    const url = encodeURIComponent(botUrl);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
    feedbackService.playSound('tap');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 255, 255, 0.3)',
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
          pb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            background: 'linear-gradient(90deg, #00ffff, #8a2be2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
          }}
        >
          {t('qrShareTitle')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'cyan' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('qrLoadingReferral')}</Typography>
          </Box>
        )}
        
        {/* Referral Stats Banner */}
        {referralData && referralData.stats.successfulReferrals > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              mb: 2,
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))',
              border: '1px solid rgba(16, 185, 129, 0.5)',
              borderRadius: 2,
            }}
          >
            <GiftIcon sx={{ color: '#10b981' }} />
            <Typography sx={{ color: '#10b981', fontWeight: 600 }}>
              {t('qrSuccessfulReferrals', 
                referralData.stats.successfulReferrals.toString(),
                referralData.stats.successfulReferrals !== 1 ? 's' : ''
              )}
            </Typography>
          </Box>
        )}

        {/* QR Code */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 3,
            background: 'white',
            borderRadius: 2,
            mb: 3,
          }}
        >
          <QRCodeSVG
            value={botUrl}
            size={200}
            level="H"
            includeMargin
            imageSettings={{
              src: '/icon-192.png',
              x: undefined,
              y: undefined,
              height: 40,
              width: 40,
              excavate: true,
            }}
          />
        </Box>

        {/* Referral Info */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            {t('qrScanOrShare')}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              p: 1,
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 1,
            }}
          >
            {botUrl}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleCopyLink}
            sx={{
              borderColor: copied ? '#10b981' : 'rgba(0, 255, 255, 0.5)',
              color: copied ? '#10b981' : 'cyan',
              '&:hover': {
                borderColor: 'cyan',
                background: 'rgba(0, 255, 255, 0.1)',
              },
            }}
          >
            {copied ? t('qrCopied') : t('qrCopyLink')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={handleShareViaTelegram}
            sx={{
              background: 'linear-gradient(135deg, #0088cc, #0099dd)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0099dd, #00aaee)',
              },
            }}
          >
            {t('qrShareButton')}
          </Button>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />

        {/* Referral Rewards Info */}
        <Typography
          variant="subtitle2"
          sx={{ color: 'cyan', fontWeight: 600, mb: 1.5 }}
        >
          {t('qrEarnCredits')}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              {t('qrFreeUserSignup')}
            </Typography>
            <Chip 
              label="$5" 
              size="small" 
              sx={{ 
                background: 'rgba(16, 185, 129, 0.2)', 
                color: '#10b981',
                fontWeight: 600,
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              {t('qrUserConvertsPaid')}
            </Typography>
            <Chip 
              label="$25" 
              size="small" 
              sx={{ 
                background: 'rgba(16, 185, 129, 0.2)', 
                color: '#10b981',
                fontWeight: 600,
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              {t('qrBusinessSignup')}
            </Typography>
            <Chip 
              label={t('qrFreeMonth')} 
              size="small" 
              sx={{ 
                background: 'rgba(138, 43, 226, 0.2)', 
                color: '#a78bfa',
                fontWeight: 600,
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              {t('qrFiveReferrals')}
            </Typography>
            <Chip 
              label={t('qrFreeMonth')} 
              size="small" 
              sx={{ 
                background: 'rgba(251, 191, 36, 0.2)', 
                color: '#fbbf24',
                fontWeight: 600,
              }} 
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QRShareDialog;
